import { type JSX, type ReactNode, useCallback, useMemo } from 'react'
import {
  ComboboxAsync,
  type ComboboxAsyncOption,
} from '#src/components/core/combobox/ComboboxAsync.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import {
  loadReferenceOptions,
  referenceNoun,
} from '#src/components/reference-picker/loadReferenceOptions.ts'
import { useReportPopoverOpen } from '#src/components/rich-text-editor/ObservationNode/observationFrameEngagement.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export interface ReferenceAnswer {
  reference: string
  display: string
}

interface ObservationReferencePickerProps {
  // Patient, Practitioner, Organization or RelatedPerson.
  resourceType: string
  // Narrows a contact search to the participant's own contacts.
  patientId: string | undefined
  // The record already picked, if any.
  value?: string | null
  onSelect: (answer: ReferenceAnswer) => void
  children: ReactNode
  // Open the search straight away, for changing a record in place.
  initialOpen?: boolean
}

// Searches the records an observation can be answered with. When staff are
// asked for, the signed-in practitioner is offered first, as a suggestion
// only: nothing is recorded until a record is picked.
export function ObservationReferencePicker({
  resourceType,
  patientId,
  value,
  onSelect,
  children,
  initialOpen = false,
}: ObservationReferencePickerProps): JSX.Element {
  const medplum = useMedplum()
  const practitioner = useCurrentPractitioner()
  const reportOpen = useReportPopoverOpen(initialOpen)
  const selected = useMemo(() => (value ? [value] : []), [value])

  const localOptions = useMemo((): ComboboxAsyncOption<ReferenceAnswer>[] => {
    const reference = practitioner.reference.reference
    if (resourceType !== 'Practitioner' || !reference) {
      return []
    }
    return [
      {
        value: { reference, display: practitioner.name },
        label: (
          <div className="flex w-full items-center justify-between gap-2">
            <PractitionerNameplate practitioner={reference} />
            <span className="shrink-0 text-xs text-muted-foreground">You</span>
          </div>
        ),
        keywords: [practitioner.name, 'you', 'me'],
      },
    ]
  }, [resourceType, practitioner])

  const loadOptions = useCallback(
    async ({
      input,
      signal,
    }: {
      input: string
      signal: AbortSignal
    }): Promise<ComboboxAsyncOption<ReferenceAnswer>[]> => {
      const options = await loadReferenceOptions({
        medplum,
        resourceType,
        input,
        patientId,
        selected,
      })
      return signal.aborted
        ? []
        : options.map(({ value: reference, display, label, keywords }) => ({
            value: { reference, display },
            label,
            keywords,
          }))
    },
    [medplum, resourceType, patientId, selected]
  )

  return (
    <ComboboxAsync<ReferenceAnswer>
      queryKey={['observation-reference', resourceType, patientId ?? '']}
      searchPlaceholder={`Search ${referenceNoun(resourceType)}s…`}
      getValue={getAnswerReference}
      localOptions={localOptions}
      loadOptions={loadOptions}
      onSelect={(answer) => {
        if (answer) {
          onSelect(answer)
        }
      }}
      initialOpen={initialOpen}
      onOpenChange={reportOpen}
      // Non-modal, so a click on the node's own note closes the search and
      // reaches the note in one go.
      modal={false}
    >
      {children}
    </ComboboxAsync>
  )
}

function getAnswerReference(answer: ReferenceAnswer): string {
  return answer.reference
}
